// components/BizCard.js
// BuzzInApp — Tarjeta de comercio
//
// Props:
//   business       object del Sheet (id, name, type, address, icon, image_url,
//                  prize_user, prize_clicks_needed, prize_friends,
//                  featured, plan, distanceMeters)
//   clickCount     number — clicks actuales del usuario para este comercio
//   canClaim       boolean
//   onShare        () => void
//   onClaim        () => void

import React from "react";
import useTranslation from "../i18n/useTranslation";
import "./BizCard.css";

// ─── Íconos de acción ─────────────────────────────────────────────────────────
const IconShare = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="1.8"/>
    <circle cx="6"  cy="12" r="3" stroke="currentColor" strokeWidth="1.8"/>
    <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M8.59 13.51L15.42 17.49M15.41 6.51L8.59 10.49"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const IconQR = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
    <rect x="3"  y="3"  width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
    <rect x="14" y="3"  width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
    <rect x="3"  y="14" width="7" height="7" rx="1" stroke="currentColor" strokeWidth="1.8"/>
    <rect x="5"  y="5"  width="3" height="3" fill="currentColor"/>
    <rect x="16" y="5"  width="3" height="3" fill="currentColor"/>
    <rect x="5"  y="16" width="3" height="3" fill="currentColor"/>
    <path d="M14 14H17V17H14V14ZM17 17H20V20H17V17ZM14 17H14V20H14V17Z"
      fill="currentColor"/>
  </svg>
);

const IconLock = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <rect x="5" y="11" width="14" height="10" rx="2"
      stroke="currentColor" strokeWidth="1.8"/>
    <path d="M8 11V7a4 4 0 0 1 8 0v4"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const IconPin = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"
      fill="var(--text-secondary)"/>
    <circle cx="12" cy="9" r="2.5" fill="var(--bg-surface)"/>
  </svg>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDistance = (meters) => {
  if (meters === null || meters === undefined) return null;
  if (meters < 1000) return `${Math.round(meters)}m`;
  return `${(meters / 1000).toFixed(1)}km`;
};

const PLAN_COLORS = {
  premium: "#F5A623",
  pro:     "var(--accent)",
  basic:   "var(--info)",
  free:    "var(--text-muted)",
};

// ─── Componente ───────────────────────────────────────────────────────────────
const BizCard = ({
  business,
  clickCount = 0,
  canClaim   = false,
  onShare,
  onClaim,
}) => {
  const { t } = useTranslation();

  if (!business) return null;

  const {
    name,
    type,
    icon,
    image_url,
    prize_user,
    prize_clicks_needed,
    prize_friends,
    featured,
    plan,
    distanceMeters,
  } = business;

  const needed      = prize_clicks_needed ?? 1;
  const progressPct = Math.min(100, Math.round((clickCount / needed) * 100));
  const remaining   = Math.max(0, needed - clickCount);
  const distLabel   = formatDistance(distanceMeters);
  const planColor   = PLAN_COLORS[plan?.toLowerCase()] ?? PLAN_COLORS.free;

  // Clase dinámica para el fill de la barra
  const fillClass = [
    "bizcard__progress-fill",
    progressPct >= 100 ? "bizcard__progress-fill--done"
      : progressPct > 0 ? "bizcard__progress-fill--active"
      : "",
  ].join(" ").trim();

  return (
    <article className={`bizcard ${featured ? "bizcard--featured" : ""}`}>

      {/* Header: icono + nombre + badges */}
      <div className="bizcard__header">
        <div className="bizcard__icon" aria-hidden="true">
          {image_url ? (
            <img
              src={image_url}
              alt={name}
              className="bizcard__icon-img"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextElementSibling.style.display = "flex";
              }}
            />
          ) : null}
          <span
            className="bizcard__icon-emoji"
            style={{ display: image_url ? "none" : "flex" }}
          >
            {icon || "🏪"}
          </span>
        </div>

        <div className="bizcard__meta">
          <div className="bizcard__name-row">
            <h3 className="bizcard__name">{name}</h3>
            {featured && (
              <span className="bizcard__badge bizcard__badge--featured">
                {t("home.featured")}
              </span>
            )}
            {plan && plan !== "free" && (
              <span
                className="bizcard__badge"
                style={{ "--badge-color": planColor }}
              >
                {plan.toUpperCase()}
              </span>
            )}
          </div>

          <div className="bizcard__subrow">
            {type && <span className="bizcard__type">{type}</span>}
            {distLabel && (
              <span className="bizcard__distance">
                <IconPin /> {distLabel}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Premio */}
      <div className="bizcard__prize">
        <div className="bizcard__prize-user">
          {t("bizCard.prize", { prize: prize_user })}
        </div>
        {prize_friends && (
          <div className="bizcard__prize-friends">
            {t("bizCard.friendPrize", { prize: prize_friends })}
          </div>
        )}
      </div>

      {/* Barra de progreso — MEJORADA */}
      <div>
        <div className="bizcard__progress-header">
          <span className={`bizcard__progress-count ${clickCount > 0 ? "bizcard__progress-count--active" : ""}`}>
          <span>{clickCount}</span>/{needed} clicks
          </span>
          <span className="bizcard__progress-needed">
            {progressPct >= 100
              ? "✓ Listo para canjear"
              : `Faltan ${remaining}`}
          </span>
        </div>
        <div
          className="bizcard__progress-bar"
          role="progressbar"
          aria-valuenow={clickCount}
          aria-valuemin={0}
          aria-valuemax={needed}
          aria-label={t("bizCard.clicksProgress", { current: clickCount, needed })}
        >
          <div className={fillClass} style={{ width: `${progressPct}%` }} />
        </div>
      </div>

      {/* Acciones */}
      <div className="bizcard__actions">
        <button
          className="bizcard__btn bizcard__btn--share"
          onClick={onShare}
          aria-label={`${t("bizCard.shareButton")} ${name}`}
        >
          <IconShare />
          {t("bizCard.shareButton")}
        </button>

        <button
          className={`bizcard__btn bizcard__btn--claim ${canClaim ? "bizcard__btn--claim-ready" : ""}`}
          onClick={canClaim ? onClaim : undefined}
          disabled={!canClaim}
          aria-label={
            canClaim
              ? `${t("bizCard.claimButton")} ${name}`
              : `Necesitás ${remaining} clicks más para canjear`
          }
        >
          {canClaim ? <IconQR /> : <IconLock />}
          {canClaim
            ? t("bizCard.claimButton")
            : remaining > 0
              ? `${remaining} más`
              : t("bizCard.claimButton")}
        </button>
      </div>
    </article>
  );
};

export default BizCard;